// JoinGameScene.js
import { joinGame } from "../services/multiplayerService.js";
import MenuUIUtils from "../ui/MenuUIUtils.js";
import BaseMenuScene from "./BaseMenuScene.js";
import { auth, db } from "../firebaseConfig.js";
import { pollGameUpdates } from '../firebaseRestApi.js'; // Add this line
import { hasSpecialAccess, getSpecialAccessUserId, getSpecialAccessDisplayName } from "../services/specialAccessService.js";


class JoinGameScene extends BaseMenuScene {
  constructor() {
    super({ key: "JoinGameScene" });
    this.gameCode = ""; // Initial reset in constructor is fine too
    this.uiElements = {};
    this.keyboardButtons = [];
    this.codeDisplay = null; // Initialize as null
  }
   // --- ADD or MODIFY init method ---
   init(data) {
    super.init(data); // Call parent init if extending BaseMenuScene
    console.log(`[${this.scene.key}] Initializing...`);
    this.gameCode = ""; // Reset game code here
    this.codeDisplay = null; // Reset reference
    // Reset any other scene-specific state variables here
}

  create() {
    this.setRibbonVisibility(false);
        super.create();
    this.cameras.main.setBackgroundColor("#000000");

    // Title
    const title = MenuUIUtils.createTitle(this, "JOIN GAME", 80, {
      fontSize: "48px",
      color: "#00FF00"
    });
    this.menuItems.push(title);

    // Code label
    const codeLabel = this.add.text(
      this.cameras.main.width / 2,
      150,
      "ENTER GAME CODE:",
      { fontFamily: 'Arial', fontSize: '24px', color: '#FFFFFF' }
    ).setOrigin(0.5);
    this.menuItems.push(codeLabel);

    // Code display background
    const codeDisplayBg = this.add.rectangle(
      this.cameras.main.width / 2,
      200,
      300, 50,
      0x111111
    ).setOrigin(0.5);
    const codeDisplayBorder = this.add.rectangle(
      this.cameras.main.width / 2,
      200,
      300, 50,
      0x333333
    ).setOrigin(0.5);
    codeDisplayBorder.setStrokeStyle(2, 0x00FF00);

    // Code display text
    this.codeDisplay = this.add.text(
      this.cameras.main.width / 2,
      200,
      "______",
      { fontFamily: 'Arial', fontSize: '32px', color: '#00FF00', align: 'center' }
    ).setOrigin(0.5);

    // Virtual keyboard
    this.createVirtualKeyboard(280);

    // Action buttons
    const buttonsY = 520;
    const buttonSpacing = 70;

    // Join button
    const joinBtnRect = this.add.rectangle(
      this.cameras.main.width / 2,
      buttonsY,
      250, 60,
      0x00AA00
    ).setInteractive({ useHandCursor: true });
    const joinText = this.add.text(
      joinBtnRect.x,
      joinBtnRect.y,
      "JOIN GAME",
      { fontFamily: 'Arial', fontSize: '24px', color: '#FFFFFF', fontWeight: 'bold' }
    ).setOrigin(0.5);

    joinBtnRect.on('pointerdown', async () => {
      if (this.gameCode.length !== 6) {
          this.showMessage("Please enter a valid 6-character code");
          return;
      }
  
      // --- MODIFIED AUTH CHECK ---
      const user = auth.currentUser;
      const specialAccess = hasSpecialAccess();
      let userIdToJoin = null;
      let displayNameToJoin = null;
  
      if (user) {
          userIdToJoin = user.uid;
          // Attempt to get profile name, fallback to email/default
          try {
              const profileDoc = await db.collection("userProfiles").doc(user.uid).get();
              displayNameToJoin = (profileDoc.exists && profileDoc.data().displayName)
                                  ? profileDoc.data().displayName
                                  : (user.displayName || user.email || "Player");
          } catch (e) {
              console.warn("Error fetching profile name for join:", e);
              displayNameToJoin = user.displayName || user.email || "Player";
          }
      } else if (specialAccess) {
          userIdToJoin = getSpecialAccessUserId();
          displayNameToJoin = getSpecialAccessDisplayName();
      } else {
          // Neither logged in nor Guest+
          this.showMessage("Please sign in or use an access code to join a game");
          return; // Exit if no valid identity
      }
      // --- END MODIFIED AUTH CHECK ---
  
      // Update button appearance (keep this)
      joinBtnRect.fillColor = 0x008800;
      joinText.setText("JOINING...");
      joinBtnRect.input.enabled = false;
  
      const resetTimeout = setTimeout(() => { /* ... keep timeout logic ... */ }, 15000);
  
      try {
          console.log(`Attempting to join game ${this.gameCode} as user ${userIdToJoin} (${displayNameToJoin})`);
  
          // --- MODIFIED: Pass correct ID and Name ---
          await joinGame(this.gameCode, userIdToJoin, displayNameToJoin);
          // --- END MODIFIED ---
  
          clearTimeout(resetTimeout);
          this.startScene("LobbyScene", { gameCode: this.gameCode, isHost: false });
  
      } catch (error) {
          clearTimeout(resetTimeout);
          console.error("Error joining game:", error);
          // Reset button appearance (keep this)
          joinBtnRect.fillColor = 0x00AA00;
          joinText.setText("JOIN GAME");
          joinBtnRect.input.enabled = true;
        
        // Show user-friendly error message
        if (error.message && error.message.includes("not found")) {
          this.showMessage("Game not found. Please check the code and try again.");
        } else if (error.message && error.message.includes("already started")) {
          this.showMessage("This game has already started or ended.");
        } else if (error.message && error.message.includes("timeout")) {
          this.showMessage("Connection timed out. Please check your internet and try again.");
        } else {
          this.showMessage("Error joining game: " + (error.message || "Unknown error"));
        }
      }
    });

    // Back button
    const backBtnRect = this.add.rectangle(
      this.cameras.main.width / 2,
      buttonsY + buttonSpacing,
      250, 60,
      0x555555
    ).setInteractive({ useHandCursor: true });
    const backText = this.add.text(
      backBtnRect.x,
      backBtnRect.y,
      "BACK",
      { fontFamily: 'Arial', fontSize: '24px', color: '#FFFFFF' }
    ).setOrigin(0.5);

    backBtnRect.on('pointerdown', () => {
      backBtnRect.fillColor = 0x444444;
      this.time.delayedCall(100, () => {
        backBtnRect.fillColor = 0x555555;
      });
      this.startScene("MultiplayerMenuScene");
    });

    
    // --- ENSURE THESE LINES ARE AT THE END ---
    if (window.UIController) {
      window.UIController.setActiveScene(this);
      // Optionally, be more explicit about enabling input if needed,
      // though setActiveScene should ideally trigger enableSceneInput if no overlays are shown.
      // window.UIController.enableSceneInput(); // Can add this for extra robustness
  } else {
      console.warn(`UIController not available in ${this.scene.key}`);
  }
   console.log(`[${this.scene.key}] create method finished.`);

  }

  // Add this cleanup method to JoinGameScene


createVirtualKeyboard(startY) {
  this.keyboardContainer = this.add.container(this.cameras.main.width / 2, startY);
  // Clear previous buttons if any
  this.keyboardButtons = [];

  const keyLayout = [
    ["A", "B", "C", "D", "E", "F", "G", "H"],
    ["J", "K", "L", "M", "N", "P", "Q", "R"],
    ["S", "T", "U", "V", "W", "X", "Y", "Z"],
    ["2", "3", "4", "5", "6", "7", "8", "9"]
  ];

  const keySize = 40;
  const keySpacing = 50;
  const rowSpacing = 50;

  keyLayout.forEach((row, rowIndex) => {
    const rowWidth = row.length * keySpacing;
    const startX = -rowWidth / 2 + keySpacing / 2;
    const y = rowIndex * rowSpacing;
    row.forEach((key, keyIndex) => {
      const x = startX + (keyIndex * keySpacing);
      const keyShadow = this.add.circle(x + 2, y + 2, keySize / 2, 0x000000, 0.5);
      const keyBg = this.add.circle(x, y, keySize / 2, 0x4477aa);
      const keyText = this.add.text(x, y, key, {
        fontFamily: 'Arial',
        fontSize: '20px',
        color: '#FFFFFF',
        fontWeight: 'bold'
      }).setOrigin(0.5);

      // Store the button for later access
      this.keyboardButtons.push(keyBg);

      keyBg.setInteractive({ useHandCursor: true });
      keyBg.on('pointerdown', () => {
        keyBg.fillColor = 0x3366aa;
        this.time.delayedCall(100, () => {
          keyBg.fillColor = 0x4477aa;
        });
        if (this.gameCode.length < 6) {
          this.gameCode += key;
          this.updateCodeDisplay();
        }
      });

      this.keyboardContainer.add(keyShadow);
      this.keyboardContainer.add(keyBg);
      this.keyboardContainer.add(keyText);
    });
  });

  // Delete button
  const lastRowY = (keyLayout.length - 1) * rowSpacing;
  const delX = (keyLayout[keyLayout.length - 1].length * keySpacing) / 2 + 40;

  const delShadow = this.add.rectangle(delX + 2, lastRowY + 2, 70, 36, 0x000000, 0.5);
  const delKey = this.add.rectangle(delX, lastRowY, 70, 36, 0xcc3333);
  const delText = this.add.text(delX, lastRowY, "DEL", {
    fontFamily: 'Arial', fontSize: '18px', color: '#FFFFFF', fontWeight: 'bold'
  }).setOrigin(0.5);

  // Store delete button
  this.keyboardButtons.push(delKey);

  delKey.setInteractive({ useHandCursor: true });
  delKey.on('pointerdown', () => {
    delKey.fillColor = 0xaa2222;
    this.time.delayedCall(100, () => {
      delKey.fillColor = 0xcc3333;
    });
    if (this.gameCode.length > 0) {
      this.gameCode = this.gameCode.slice(0, -1);
      this.updateCodeDisplay();
    }
  });

  this.keyboardContainer.add(delShadow);
  this.keyboardContainer.add(delKey);
  this.keyboardContainer.add(delText);
}

  updateCodeDisplay() {
    let displayText = "";
    for (let i = 0; i < 6; i++) {
      if (i < this.gameCode.length) {
        displayText += this.gameCode[i];
      } else {
        displayText += "_";
      }
      if (i < 5) {
        displayText += " ";
      }
    }
    this.codeDisplay.setText(displayText); // This should now be safe
  }

  showMessage(message, duration = 2000) {
    const overlay = this.add.rectangle(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
      400, 200,
      0x000000, 0.8
    ).setDepth(1000).setOrigin(0.5);

    const text = this.add.text(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
      message,
      { fontFamily: 'Arial', fontSize: '20px', color: '#FFFFFF', align: 'center', wordWrap: { width: 380 } }
    ).setOrigin(0.5).setDepth(1001);

    const closeBtn = this.add.rectangle(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2 + 50,
      100, 36,
      0x555555
    ).setInteractive({ useHandCursor: true }).setDepth(1001).setOrigin(0.5);

    const closeText = this.add.text(
      closeBtn.x, closeBtn.y,
      "OK",
      { fontFamily: 'Arial', fontSize: '18px', color: '#FFFFFF' }
    ).setOrigin(0.5).setDepth(1002);

    closeBtn.on('pointerdown', () => {
      overlay.destroy();
      text.destroy();
      closeBtn.destroy();
      closeText.destroy();
    });

    this.time.delayedCall(duration, () => {
      if (overlay.active) {
        overlay.destroy();
        text.destroy();
        closeBtn.destroy();
        closeText.destroy();
      }
    });
  }

  handleResize(gameSize) {
    super.handleResize(gameSize);
    if (!this.cameras || !this.cameras.main) return;
  
    const centerX = this.cameras.main.width / 2;
    
    // Fix keyboard container position
    if (this.keyboardContainer) {
      this.keyboardContainer.x = centerX;
    }
    
    // Fix code display position
    if (this.codeDisplay) {
      this.codeDisplay.x = centerX;
    }
  }
  

  shutdown() {
    console.log(`[${this.scene.key}] Shutting down...`);
    // Reset non-Phaser state
    this.gameCode = "";

    // Clean up timers (Phaser might do this, but explicit is okay)
    if (this.time) {
      this.time.removeAllEvents();
    }

    // Clean up keyboard container and buttons
    if (this.keyboardContainer) {
       try { this.keyboardContainer.destroy(true); } catch(e){} // Destroy container and children
       this.keyboardContainer = null;
    }
    this.keyboardButtons = [];

     // Clear references to Phaser objects that will be destroyed by super.shutdown() or SceneManager
     this.codeDisplay = null;
     // Clear other references like title, labels, buttons if stored on `this`

    super.shutdown(); // Call parent shutdown LAST
    console.log(`[${this.scene.key}] Shutdown complete.`);
  }

}

export default JoinGameScene;
